import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  delay,
  SectionHeading,
  Stage,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';
import { COMPLIANCE, ShieldGlyph } from '@/components/b2b/kit';

import { TRUST_CENTER_URL, useAboutTranslation } from './kit';

export default function AboutValues() {
  const { t } = useAboutTranslation();
  const { ref, state } = useReveal<HTMLOListElement>(0.2);

  const values = [
    {
      title: t('Simple first'),
      text: t(
        'Anyone should get a great result on the first try, without a manual or a guide to prompts.',
      ),
    },
    {
      title: t('Tasks, not jobs'),
      text: t(
        'AI should take the repetitive work off your plate and leave the ideas, judgement and relationships to you.',
      ),
    },
    {
      title: t('Built with our customers'),
      text: t(
        'Your feedback decides what we build next, and we ship improvements all the time.',
      ),
    },
    {
      title: t('Trust is earned'),
      text: t(
        'We don’t use your inputs to train our own models, and our security program follows SOC 2 and ISO 27001.',
      ),
    },
  ];

  return (
    <section id='values' className='scroll-mt-8 py-6 sm:py-8'>
      <div className='mx-auto max-w-7xl px-3 sm:px-6 lg:px-8'>
        <Stage
          variant='lavender'
          halftoneAt='100% 100%'
          halftoneReach={45}
          className='px-6 py-14 sm:px-12 sm:py-20 lg:px-16'
        >
          <div className='grid gap-12 lg:grid-cols-12 lg:gap-16'>
            <SectionHeading
              size='md'
              className='lg:col-span-5'
              title={t('Automate tasks, not jobs.')}
              tail={t('That’s the idea behind everything we build.')}
              lead={t(
                'Our mission is to make AI simple, useful and safe for everyone, so people spend less time on busywork and more on the work that needs them.',
              )}
            />

            <ol
              ref={ref}
              data-reveal={state}
              className={clsxm(
                styles.reveal,
                'grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:col-span-7 lg:pt-2',
              )}
            >
              {values.map(({ title, text }, index) => (
                <li
                  key={title}
                  className={clsxm(
                    styles.rv,
                    'border-t border-[#0f0f28]/10 pt-5 dark:border-white/10',
                  )}
                  style={delay(index * 90)}
                >
                  <span
                    dir='ltr'
                    className='text-sm font-semibold tabular-nums text-primary-600 dark:text-primary-400'
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <h3
                    className={clsxm(
                      'mt-3 text-xl font-semibold tracking-[-0.015em]',
                      tone.ink,
                    )}
                  >
                    {title}
                  </h3>
                  <p className={clsxm('mt-2 text-[15px] leading-7', tone.body)}>
                    {text}
                  </p>
                </li>
              ))}
            </ol>
          </div>

          <div className='mt-14 flex flex-col gap-5 rounded-[20px] bg-white/70 px-5 py-4 ring-1 ring-[#0f0f28]/[0.05] sm:flex-row sm:items-center sm:justify-between sm:px-6 dark:bg-white/[0.04] dark:ring-white/[0.06]'>
            <div
              className={clsxm(
                'flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] font-medium',
                tone.body,
              )}
            >
              {COMPLIANCE.map((name) => (
                <span key={name} className='flex items-center gap-1.5'>
                  <ShieldGlyph className='h-4 w-4 text-emerald-600 dark:text-emerald-400' />
                  {name}
                </span>
              ))}
            </div>
            <a
              href={TRUST_CENTER_URL}
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex flex-none items-center gap-1.5 text-sm font-semibold text-primary-600 underline-offset-4 hover:underline dark:text-primary-400'
            >
              {t('Visit our Trust Center')}
              <ArrowGlyph className='h-3.5 w-3.5 -rotate-45 rtl:rotate-45' />
            </a>
          </div>
        </Stage>
      </div>
    </section>
  );
}
